import { Alert, Box, Button, FileInput, Image, Stack, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconTrash, IconUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_SIGNOFF_LENGTH = 2000;

type SignatureEditorProps = {
  downloadUrl: string | null;
  hasSignature: boolean;
  savedSignoffText: string | null;
  isUploading: boolean;
  isDeleting: boolean;
  isSavingSignoff: boolean;
  onUpload: (file: File) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  onSaveSignoff: (signoffText: string) => Promise<unknown>;
};

export default function SignatureEditor({
  downloadUrl,
  hasSignature,
  savedSignoffText,
  isUploading,
  isDeleting,
  isSavingSignoff,
  onUpload,
  onDelete,
  onSaveSignoff,
}: SignatureEditorProps) {
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [signoffText, setSignoffText] = useState('');

  useEffect(() => {
    setSignoffText(savedSignoffText ?? '');
  }, [savedSignoffText]);

  const validateFile = (file: File | null): string | null => {
    if (!file) return null;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'File must be a PNG or JPEG image.';
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return `File size (${sizeMB}MB) exceeds maximum allowed size of ${MAX_FILE_SIZE_MB}MB.`;
    }

    return null;
  };

  const handleFileChange = (selectedFile: File | null) => {
    const error = validateFile(selectedFile);
    setValidationError(error);
    setFile(error ? null : selectedFile);

    if (error) {
      notifications.show({
        title: 'Invalid file',
        message: error,
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await onUpload(file);
      setFile(null);
      setValidationError(null);
      notifications.show({
        title: 'Signature uploaded',
        message: 'The signature has been uploaded successfully.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Upload failed',
        message: 'An error occurred while uploading the signature.',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete();
      notifications.show({
        title: 'Signature removed',
        message: 'The signature has been removed.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Delete failed',
        message: 'An error occurred while removing the signature.',
        color: 'red',
      });
    }
  };

  const handleSaveSignoff = async () => {
    try {
      await onSaveSignoff(signoffText.trim());
      notifications.show({
        title: 'Sign-off saved',
        message: 'The sign-off text has been saved.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Save failed',
        message: 'An error occurred while saving the sign-off text.',
        color: 'red',
      });
    }
  };

  return (
    <Stack>
      {hasSignature && downloadUrl && (
        <Box>
          <Image src={downloadUrl} alt="Signature" maw={300} radius="md" mb="xs" />
          <Button
            variant="subtle"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={handleDelete}
            loading={isDeleting}
          >
            Remove signature
          </Button>
        </Box>
      )}

      <FileInput
        label="Signature image"
        description="PNG or JPEG format, max 2MB"
        placeholder="Click to select file"
        accept="image/png,image/jpeg,image/jpg"
        value={file}
        onChange={handleFileChange}
        leftSection={<IconUpload size={16} />}
        maw={400}
        error={validationError}
      />

      {validationError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {validationError}
        </Alert>
      )}

      <Button
        onClick={handleUpload}
        loading={isUploading}
        disabled={!file || !!validationError}
        leftSection={<IconUpload size={16} />}
        w="fit-content"
      >
        Upload signature
      </Button>

      <Textarea
        label="Sign-off text"
        description="Shown below the signature in documents, e.g. name, role and contact details"
        placeholder={'Jane Smith\nFinancial adviser\njane.smith@example.co.uk'}
        value={signoffText}
        onChange={e => setSignoffText(e.currentTarget.value)}
        autosize
        minRows={3}
        maxRows={8}
        maxLength={MAX_SIGNOFF_LENGTH}
        maw={400}
      />

      <Button
        onClick={handleSaveSignoff}
        loading={isSavingSignoff}
        disabled={(savedSignoffText ?? '') === signoffText.trim()}
        w="fit-content"
      >
        Save sign-off
      </Button>
    </Stack>
  );
}
